import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { OrganizationServices, UserServices } from '@/services/apis';

interface StateType {
  organizations?: any[];
  loading?: boolean;
  error?: boolean;
  errorMessage?: string;
  totalRecords?: number;
  organizationAdmins?: any[];
  totalOrganizationAdminsCount?: number;
  organizationsCreatedInSetup?: any[];
  organizationsCreatedInSetupCount?: number;
}

const initialState: StateType = {
  organizations: [],
  loading: true,
  error: false,
  errorMessage: '',
  totalRecords: 0,
  organizationAdmins: [],
  totalOrganizationAdminsCount: 0,
  organizationsCreatedInSetup: [],
  organizationsCreatedInSetupCount: 0
};

export const fetchAllOrganizations = createAsyncThunk(
  'organization/fetchAllOrganizations',
  async ({ domainName, page, rowsPerPage, searchOrganizationTerm, orgId }: any) => {
    const response = await OrganizationServices.fetchOrganizations({
      domainName,
      pageNumber: page,
      pageSize: rowsPerPage,
      organizationName: searchOrganizationTerm,
      orgId: orgId
    });
    return response;
  }
);

export const fetchNewCreatedOrganizations = createAsyncThunk(
  'organization/fetchNewCreatedOrganizations',
  async ({ domainName, page, rowsPerPage, searchOrganizationTerm, orgId }: any) => {
    const response = await OrganizationServices.fetchOrganizations({
      domainName,
      pageNumber: page,
      pageSize: rowsPerPage,
      organizationName: searchOrganizationTerm,
      orgId: orgId
    });
    return response;
  }
);

export const fetchOrgAdmins = createAsyncThunk(
  'users/fetchOrganizationAdmins',
  async ({
    pagination,
    userName,
    domainName,
    roleType,
    roleName,
    groupId,
    roleId,
    organizationId,
    appType,
    appName,
    organizationName
  }: any) => {
    const response = await UserServices.fetchUsers(
      pagination.page + 1,
      pagination.rowsPerPage,
      userName,
      domainName,
      roleType,
      roleName,
      groupId,
      roleId,
      organizationName,
      organizationId,
      appName,
      appType
    );
    return response;
  }
);

export const createNewOrganization = createAsyncThunk('organization/createNewOrganization', async (organizationData: any) => {
  const data = await OrganizationServices.createOrganization(organizationData);
  return data;
});

export const updateOrganization = createAsyncThunk('organization/updateOrganization', async (organizationData: any) => {
  const { orgId, ...finalData } = organizationData;
  const data = await OrganizationServices.updateOrganization(finalData, orgId);
  return data;
});

export const deleteOrganization = createAsyncThunk('organization/deleteOrganization', async (organization: any) => {
  const data = await OrganizationServices.deleteOrganization(organization);
  return data;
});

export const downloadOrganization = createAsyncThunk('organization/downloadOrganization', async () => {
  const response = await OrganizationServices.downloadOrganization();
  return response;
});

export const OrganizationSlice = createSlice({
  name: 'organization',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchAllOrganizations.pending, (state) => {});
    builder.addCase(fetchAllOrganizations.fulfilled, (state, { payload }) => {
      return { ...state, organizations: payload?.result?.organizations, totalRecords: payload?.totalRecords, loading: false };
    });
    builder.addCase(fetchAllOrganizations.rejected, (state, { payload }) => {
      return { ...state, organizations: [], totalRecords: 0, loading: false };
    });
    builder.addCase(fetchNewCreatedOrganizations.pending, (state) => {});
    builder.addCase(fetchNewCreatedOrganizations.fulfilled, (state, { payload }) => {
      return {
        ...state,
        organizationsCreatedInSetup: payload?.result?.organizations,
        organizationsCreatedInSetupCount: payload?.totalRecords,
        loading: false
      };
    });
    builder.addCase(fetchNewCreatedOrganizations.rejected, (state, { payload }) => {
      return { ...state, organizationsCreatedInSetup: [], organizationsCreatedInSetupCount: 0, loading: false };
    });
    builder.addCase(fetchOrgAdmins.pending, (state) => {
      return { ...state, loading: true };
    });
    builder.addCase(fetchOrgAdmins.fulfilled, (state, { payload }) => {
      const finalUsers = payload?.result?.users?.map((item: any) => {
        const role: any = [];
        item.organizations.map((org: any) =>
          org.application.map((app: any) => {
            app.role.map((userrole: any) => {
              role.push({ ...userrole, app_uuid: app.id, app_name: app.name });
            });
          })
        );
        return {
          ...item,
          roles: role
        };
      });
      return {
        ...state,
        organizationAdmins: finalUsers,
        totalOrganizationAdminsCount: payload?.totalRecords,
        loading: false
      };
    });
    builder.addCase(fetchOrgAdmins.rejected, (state, { payload }) => {
      return { ...state, organizationAdmins: [], totalOrganizationAdminsCount: 0, loading: false };
    });
  }
});

export const organizationSelector = (state: any) => state.domain;

export default OrganizationSlice.reducer;
